<template>
  <v-expansion-panel v-if="funcionarioId">
    <v-expansion-panel-header>
      <span>
        <v-icon left small>mdi-file-xml-box</v-icon>
        eSocial
        <v-chip
          v-if="previa"
          x-small
          class="ml-2"
          :color="
            previa.pendencias.length ? 'orange darken-2' : 'green darken-1'
          "
          dark
        >
          {{
            previa.pendencias.length
              ? `${previa.pendencias.length} pendência(s) para a admissão`
              : "Pronto para envio"
          }}
        </v-chip>
      </span>
    </v-expansion-panel-header>
    <v-expansion-panel-content>
      <v-skeleton-loader v-if="carregando" type="article" />
      <v-alert
        v-else-if="erro"
        type="error"
        color="red darken-2"
        outlined
        dense
        >{{ erro }}</v-alert
      >
      <template v-else-if="previa">
        <div class="text-caption text--secondary mb-3">
          Dados do vínculo exigidos pelo eSocial além do cadastro. Campos em
          branco usam o valor derivado do cadastro, quando houver. Salve esta
          seção separadamente; salvar reenvia a admissão para validação.
        </div>

        <v-card outlined class="pa-3 mb-4">
          <div class="font-weight-bold mb-1">O que será enviado</div>
          <div class="text-body-2">
            Categoria:
            <strong>{{
              descricao(categorias, previa.categoriaEfetiva)
            }}</strong>
          </div>
          <div class="text-body-2" v-if="previa.regimeTrabalhistaEfetivo">
            Regime:
            <strong>{{
              previa.regimeTrabalhistaEfetivo === 2
                ? "Estatutário / legislação específica"
                : "CLT"
            }}</strong>
            — previdência:
            <strong>{{
              descricao(regimesPrevidencia, previa.regimePrevidenciaEfetivo)
            }}</strong>
          </div>
          <div class="text-body-2" v-if="previa.tipoContratoEfetivo">
            Contrato:
            <strong>{{
              previa.tipoContratoEfetivo === 2
                ? `prazo determinado até ${data(previa.terminoContratoEfetivo)}`
                : "prazo indeterminado"
            }}</strong>
            <span v-if="previa.fimExperiencia">
              (experiência até {{ data(previa.fimExperiencia) }}; a conversão em
              prazo indeterminado é enviada no dia seguinte)</span
            >
          </div>
          <v-alert
            v-if="previa.pendencias.length"
            type="warning"
            color="orange darken-4"
            text
            dense
            class="mt-3 mb-0"
          >
            <div v-for="(p, i) in previa.pendencias" :key="i">• {{ p }}</div>
          </v-alert>
        </v-card>

        <v-row dense>
          <v-col cols="12" md="8">
            <v-select
              v-model="form.categoria"
              :items="categoriasComAutomatica"
              label="Categoria eSocial (Tabela 01)"
              :disabled="!podeEditar"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="form.grauExposicao"
              :items="grausExposicao"
              label="Exposição a agentes nocivos"
              hint="Informado na remuneração (S-1200/S-2299). Em branco: não exposto. Use o grau do PPP/LTCAT."
              persistent-hint
              :disabled="!podeEditar"
              outlined
              dense
              clearable
            />
          </v-col>
          <v-col cols="12" md="4" v-if="previa.empregadorPublico">
            <v-select
              v-model="form.acumulaCargo"
              :items="simNao"
              label="Acumula cargos públicos?"
              :disabled="!podeEditar"
              outlined
              dense
              clearable
            />
          </v-col>
        </v-row>

        <template v-if="estatutario">
          <div class="font-weight-bold mt-2 mb-2">Servidor público</div>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-select
                v-model="form.regimePrevidencia"
                :items="regimesComAutomatico"
                label="Regime previdenciário"
                :disabled="!podeEditar"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="form.tipoProvimento"
                :items="provimentosComAutomatico"
                label="Tipo de provimento"
                :disabled="!podeEditar"
                outlined
                dense
              />
            </v-col>
            <template v-if="rpps">
              <v-col cols="12" md="4">
                <v-select
                  v-model="form.planoRpps"
                  :items="planosRpps"
                  label="Plano de segregação da massa"
                  :disabled="!podeEditar"
                  outlined
                  dense
                  clearable
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="form.tetoRgps"
                  :items="simNao"
                  label="Sujeito ao teto do RGPS?"
                  hint="Previdência complementar instituída"
                  persistent-hint
                  :disabled="!podeEditar"
                  outlined
                  dense
                  clearable
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="form.abonoPermanencia"
                  :items="simNao"
                  label="Recebe abono de permanência?"
                  :disabled="!podeEditar"
                  outlined
                  dense
                  clearable
                />
              </v-col>
              <v-col cols="12" md="4" v-if="form.abonoPermanencia">
                <v-text-field
                  v-model="form.abonoInicio"
                  type="date"
                  label="Início do abono de permanência"
                  :disabled="!podeEditar"
                  outlined
                  dense
                />
              </v-col>
            </template>
          </v-row>
        </template>

        <template v-if="categoria === 103">
          <div class="font-weight-bold mt-2 mb-2">Aprendiz</div>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-select
                v-model="form.aprendizModalidade"
                :items="modalidadesAprendiz"
                label="Modalidade de contratação"
                :disabled="!podeEditar"
                outlined
                dense
                clearable
              />
            </v-col>
            <v-col cols="12" md="6" v-if="form.aprendizModalidade === 1">
              <v-text-field
                v-model="form.aprendizEntidadeCnpj"
                label="CNPJ da entidade qualificadora"
                maxlength="18"
                :disabled="!podeEditar"
                outlined
                dense
              />
            </v-col>
            <template v-if="form.aprendizModalidade === 2">
              <v-col cols="12" md="2">
                <v-select
                  v-model="form.aprendizEstabelecimentoTipo"
                  :items="tiposInscricao"
                  label="Tipo"
                  :disabled="!podeEditar"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="form.aprendizEstabelecimentoInscricao"
                  label="Estabelecimento para o qual foi contratado"
                  maxlength="18"
                  :disabled="!podeEditar"
                  outlined
                  dense
                />
              </v-col>
            </template>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.aprendizPraticaCnpj"
                label="CNPJ do local das atividades práticas (opcional)"
                hint="Quando a prática ocorre em estabelecimento diferente do cumpridor da cota."
                persistent-hint
                maxlength="18"
                :disabled="!podeEditar"
                outlined
                dense
              />
            </v-col>
          </v-row>
        </template>

        <template v-if="categoria === 106">
          <div class="font-weight-bold mt-2 mb-2">
            Trabalhador temporário (Lei 6.019/1974)
          </div>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-select
                v-model="form.temporarioHipotese"
                :items="hipotesesTemporario"
                label="Hipótese legal"
                :disabled="!podeEditar"
                outlined
                dense
                clearable
              />
            </v-col>
            <v-col cols="12" md="6" v-if="form.temporarioHipotese === 1">
              <v-text-field
                v-model="form.temporarioSubstituidoCpf"
                v-mask="'###.###.###-##'"
                label="CPF do trabalhador substituído"
                :disabled="!podeEditar"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="2">
              <v-select
                v-model="form.temporarioTomadorTipo"
                :items="tiposInscricao"
                label="Tipo"
                :disabled="!podeEditar"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="form.temporarioTomadorInscricao"
                label="Tomador de serviços (CNPJ/CPF)"
                maxlength="18"
                :disabled="!podeEditar"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="form.temporarioJustificativa"
                label="Justificativa da contratação"
                counter="999"
                maxlength="999"
                rows="2"
                auto-grow
                :disabled="!podeEditar"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="form.temporarioJustificativaProrrogacao"
                label="Justificativa da prorrogação (quando o término for estendido)"
                counter="999"
                maxlength="999"
                rows="2"
                auto-grow
                :disabled="!podeEditar"
                outlined
                dense
              />
            </v-col>
          </v-row>
        </template>

        <div class="font-weight-bold mt-2 mb-2">
          Nascido no exterior ou estrangeiro
        </div>
        <v-row dense>
          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="form.paisNascimento"
              :items="paises"
              label="País de nascimento"
              hint="Em branco, usa o Brasil quando a naturalidade é brasileira."
              persistent-hint
              :disabled="!podeEditar"
              outlined
              dense
              clearable
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="form.paisNacionalidade"
              :items="paises"
              label="Nacionalidade"
              :disabled="!podeEditar"
              outlined
              dense
              clearable
            />
          </v-col>
          <template v-if="imigrante">
            <v-col cols="12" md="6">
              <v-select
                v-model="form.imigranteTempoResidencia"
                :items="temposResidencia"
                label="Tempo de residência"
                :disabled="!podeEditar"
                outlined
                dense
                clearable
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="form.imigranteCondicaoIngresso"
                :items="condicoesIngresso"
                label="Condição de ingresso"
                :disabled="!podeEditar"
                outlined
                dense
                clearable
              />
            </v-col>
          </template>
        </v-row>

        <div class="d-flex mt-2" v-if="podeEditar">
          <v-spacer />
          <v-btn
            :loading="salvando"
            @click="salvar"
            class="
              font-weight-bold
              text-none
              px-6
              btn-primary
              bg-gradient-primary
            "
            height="39"
          >
            <v-icon left>mdi-content-save-outline</v-icon>
            Salvar dados eSocial
          </v-btn>
        </div>
      </template>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>
<script>
import ESocialEnvioDataService from "@/services/pessoal/ESocialEnvioDataService";
import {
  CATEGORIAS,
  CONDICOES_INGRESSO,
  HIPOTESES_TEMPORARIO,
  MODALIDADES_APRENDIZ,
  PAISES,
  PLANOS_RPPS,
  REGIMES_PREVIDENCIA,
  SIM_NAO,
  TEMPOS_RESIDENCIA,
  TIPOS_INSCRICAO,
  TIPOS_PROVIMENTO,
  descricao,
} from "./esocialTabelas";

const CAMPOS = [
  "categoria",
  "regimePrevidencia",
  "tipoProvimento",
  "planoRpps",
  "tetoRgps",
  "abonoPermanencia",
  "abonoInicio",
  "acumulaCargo",
  "grauExposicao",
  "aprendizModalidade",
  "aprendizEntidadeCnpj",
  "aprendizEstabelecimentoTipo",
  "aprendizEstabelecimentoInscricao",
  "aprendizPraticaCnpj",
  "temporarioHipotese",
  "temporarioJustificativa",
  "temporarioTomadorTipo",
  "temporarioTomadorInscricao",
  "temporarioSubstituidoCpf",
  "temporarioJustificativaProrrogacao",
  "paisNascimento",
  "paisNacionalidade",
  "imigranteTempoResidencia",
  "imigranteCondicaoIngresso",
];

export default {
  name: "ComplementoEsocialFuncionario",
  props: { funcionarioId: { type: Number, default: null } },
  data() {
    return {
      carregando: false,
      salvando: false,
      erro: null,
      previa: null,
      form: Object.fromEntries(CAMPOS.map((c) => [c, null])),
      categorias: CATEGORIAS,
      regimesPrevidencia: REGIMES_PREVIDENCIA,
      planosRpps: PLANOS_RPPS,
      modalidadesAprendiz: MODALIDADES_APRENDIZ,
      hipotesesTemporario: HIPOTESES_TEMPORARIO,
      tiposInscricao: TIPOS_INSCRICAO,
      temposResidencia: TEMPOS_RESIDENCIA,
      condicoesIngresso: CONDICOES_INGRESSO,
      simNao: SIM_NAO,
      paises: PAISES,
      grausExposicao: [
        { value: 1, text: "1 - Não exposto" },
        { value: 2, text: "2 - Aposentadoria especial aos 15 anos" },
        { value: 3, text: "3 - Aposentadoria especial aos 20 anos" },
        { value: 4, text: "4 - Aposentadoria especial aos 25 anos" },
      ],
    };
  },
  computed: {
    podeEditar() {
      const permissoes = this.$store.state.permissoes;
      return (
        Array.isArray(permissoes) &&
        permissoes.includes("ROLE_Funcionario_Update")
      );
    },
    categoria() {
      return this.form.categoria ?? this.previa?.categoriaEfetiva ?? null;
    },
    estatutario() {
      return this.categoria >= 300;
    },
    rpps() {
      const regime =
        this.form.regimePrevidencia ?? this.previa?.regimePrevidenciaEfetivo;
      return regime === 2;
    },
    imigrante() {
      return (
        !!this.form.paisNacionalidade && this.form.paisNacionalidade !== "105"
      );
    },
    categoriasComAutomatica() {
      return [
        { value: null, text: "Automática (derivada do cadastro)" },
        ...CATEGORIAS,
      ];
    },
    regimesComAutomatico() {
      return [
        { value: null, text: "Automático pela categoria" },
        ...REGIMES_PREVIDENCIA,
      ];
    },
    provimentosComAutomatico() {
      return [
        { value: null, text: "Automático pela categoria" },
        ...TIPOS_PROVIMENTO,
      ];
    },
  },
  watch: {
    funcionarioId: { handler: "carregar", immediate: true },
  },
  methods: {
    descricao,
    data(valor) {
      return valor ? valor.split("-").reverse().join("/") : "-";
    },
    aplicar(dto) {
      this.previa = dto;
      CAMPOS.forEach((c) => (this.form[c] = dto[c] ?? null));
    },
    async carregar() {
      if (!this.funcionarioId) return;
      this.carregando = true;
      this.erro = null;
      try {
        const { data } =
          await ESocialEnvioDataService.getComplementoFuncionario(
            this.funcionarioId
          );
        this.aplicar(data);
      } catch (error) {
        this.erro =
          "Não foi possível carregar os dados eSocial do funcionário.";
      } finally {
        this.carregando = false;
      }
    },
    async salvar() {
      this.salvando = true;
      try {
        const { data } =
          await ESocialEnvioDataService.salvarComplementoFuncionario(
            this.funcionarioId,
            { ...this.form }
          );
        this.aplicar(data);
        this.$swal.fire("Sucesso", "Dados eSocial salvos.", "success");
      } catch (error) {
        const message =
          error?.response?.data?.message || "Falha ao salvar os dados eSocial.";
        this.$swal.fire("Erro", message, "error");
      } finally {
        this.salvando = false;
      }
    },
  },
};
</script>
